import React, { createContext, useContext, useMemo } from 'react';
import { TableViewType } from '@/features/ai-agents/chat/MarkdownTableVisualization/types';

export interface DefaultViewHint {
  tooltips: Record<string, string> | null;
  type: TableViewType | null;
  xAxis: string | null;
  yAxis: string | null;
}

const DEFAULT_VIEW_HINT_FENCE_REGEX =
  /```vsly\/json\/default_view\n([\s\S]*?)```/;

const HINT_VIEW_TYPE_ALIASES: Record<string, TableViewType> = {
  bar: `bar`,
  column: `bar`,
  line: `line`,
  pie: `pie`,
  table: `table`,
};

/**
 * Extracts the AI-generated default visualization hint from a chat message.
 * The agent emits a `vsly/json/default_view` fenced code block suggesting
 * which view and axes best fit the table it follows.
 */
export function parseDefaultViewHint(markdown: string): DefaultViewHint | null {
  const match = markdown.match(DEFAULT_VIEW_HINT_FENCE_REGEX);

  if (!match) {
    return null;
  }

  try {
    const parsed = JSON.parse(match[1].trim());
    const hint: DefaultViewHint = {
      tooltips: normalizeHintTooltips(parsed?.tooltips),
      type: normalizeHintViewType(parsed?.type),
      xAxis: normalizeHintAxisLabel(parsed?.[`x-axis`]),
      yAxis: normalizeHintAxisLabel(parsed?.[`y-axis`]),
    };

    return hint.type || hint.xAxis || hint.yAxis || hint.tooltips ? hint : null;
  } catch {
    return null;
  }
}

function normalizeHintViewType(rawType: unknown): TableViewType | null {
  if (typeof rawType !== `string`) {
    return null;
  }

  return HINT_VIEW_TYPE_ALIASES[rawType.trim().toLowerCase()] ?? null;
}

function normalizeHintAxisLabel(rawAxis: unknown): string | null {
  return typeof rawAxis === `string` && rawAxis.trim() ? rawAxis.trim() : null;
}

function normalizeHintTooltips(
  rawTooltips: unknown,
): Record<string, string> | null {
  if (typeof rawTooltips !== `object` || rawTooltips === null) {
    return null;
  }

  const tooltips = Object.entries(
    rawTooltips as Record<string, unknown>,
  ).reduce<Record<string, string>>((normalized, [label, description]) => {
    if (
      typeof label === `string` &&
      label.trim() &&
      typeof description === `string` &&
      description.trim()
    ) {
      normalized[label.trim()] = description.trim();
    }

    return normalized;
  }, {});

  return Object.keys(tooltips).length > 0 ? tooltips : null;
}

const DefaultViewHintContext = createContext<DefaultViewHint | null>(null);

export function DefaultViewHintProvider({
  children,
  markdown,
}: {
  children?: React.ReactNode;
  markdown: string;
}) {
  const hint = useMemo(() => parseDefaultViewHint(markdown), [markdown]);

  return (
    <DefaultViewHintContext.Provider value={hint}>
      {children}
    </DefaultViewHintContext.Provider>
  );
}

export function useDefaultViewHint() {
  return useContext(DefaultViewHintContext);
}
